import type { VariantProps } from 'class-variance-authority';
import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cn } from '@savvy-kit/lib/utils';
import { cva } from 'class-variance-authority';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border border-light py-1 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 overflow-hidden',
  {
    variants: {
      variant: {
        primary: 'border-transparent bg-primary text-white font-semibold',
        'primary-muted': 'border-transparent bg-primary-foreground-light text-primary font-medium',
        secondary: 'border-transparent bg-secondary text-white font-semibold',
        'secondary-muted': 'border-transparent bg-muted dark:bg-card text-dark font-medium',
        destructive: 'border-transparent bg-destructive text-white font-semibold',
        'destructive-muted':
          'border-transparent bg-destructive-background text-destructive-foreground-dark font-medium',
        outline: 'text-dark font-semibold',
        info: 'border-transparent bg-info text-white font-semibold',
        'info-muted': 'border-transparent bg-info-foreground-light text-info-foreground-dark font-medium',
        warning: 'border-transparent bg-warning text-white font-semibold',
        'warning-muted': 'border-transparent bg-warning-foreground-light text-warning-foreground-dark font-medium',
      },
      type: {
        default: 'px-2 h-6',
        circle: 'tabular-nums h-6 min-w-6 px-1',
        'circle-sm': 'tabular-nums h-5 min-w-5 px-0.5',
        dot: 'size-2 border-white p-0',
      },
      size: {
        default: '',
        sm: 'h-5 px-1.5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      type: 'default',
      size: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  type,
  size,
  render,
  ...props
}: useRender.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant, type, size }), className),
      },
      props,
    ),
    render,
    state: {
      slot: 'badge',
      variant: variant ?? 'primary',
      type: type ?? 'default',
      size: size ?? 'default',
    },
  });
}

export { Badge, badgeVariants };
